/* =====================================================================
 * Avatar — 공통 아바타 스타일 & 컴포넌트
 * ===================================================================== */

import React from 'react';
import styled from 'styled-components';
import { User } from 'lucide-react';
import { resolveWorkspaceModeAvatar, useTenant } from '@auw/tenant';

/* ---- 공통 squircle 스타일 토큰 ---- */

export const avatarSquircle = {
  borderRadius: '22%',
  bgLight: 'linear-gradient(145deg, #eaeaed, #e1e1e5)',
  bgDark: 'linear-gradient(145deg, #434345, #38383a)',
  colorLight: '#4a4a4e',
  colorDark: '#c8c8cc',
  shadowLight: '0 1px 4px rgba(0,0,0,0.08)',
  shadowDark: '0 1px 4px rgba(0,0,0,0.35)',
} as const;

/* ---- 범용 Squircle 아바타 래퍼 ---- */

export const SquircleWrap = styled.div<{ $size: number }>`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  min-width: ${({ $size }) => $size}px;
  flex-shrink: 0;
`;

export const SquircleCircle = styled.span<{ $size: number; $bg?: string }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: ${avatarSquircle.borderRadius};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $bg }) =>
    $bg ?? (theme.mode === 'dark' ? avatarSquircle.bgDark : avatarSquircle.bgLight)};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark' ? avatarSquircle.shadowDark : avatarSquircle.shadowLight};
  color: ${({ theme }) =>
    theme.mode === 'dark' ? avatarSquircle.colorDark : avatarSquircle.colorLight};
  font-weight: 700;
  font-size: ${({ $size }) => Math.round($size * 0.42)}px;
  line-height: 1;
  user-select: none;
  overflow: hidden;

  svg {
    color: inherit;
    flex-shrink: 0;
  }
`;

export const SquircleImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
`;

/* ---- 레거시 호환: 채팅 말풍선용 (기존 Avatar 인터페이스) ---- */

interface AvatarProps {
  role: 'user' | 'assistant';
  size?: number;
  /**
   * Assistant 전용: 답변이 "처리 중"(요청 → 타임라인 → 최종답변 스트리밍)일
   * 때 `true`. 처리 중에는 모션 아바타(`workspaceModes.agent.avatar`, 예:
   * samsung `agent-bubble.gif`)를, 완료 후에는 정적 브랜드 마크
   * (`agentLogo`, 예: `agent-bubble.png`)를 보여 준다. 모션 아바타를 안 쓰는
   * 테넌트는 두 상태 모두 `agentLogo` 로 graceful fallback.
   */
  animated?: boolean;
}

const UserWrapper = styled.div<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  min-width: ${({ $size }) => $size}px;
  border-radius: ${avatarSquircle.borderRadius};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.textInverse};
  flex-shrink: 0;
  transition: background ${({ theme }) => theme.transitions.fast};

  svg {
    width: ${({ $size }) => Math.round($size * 0.5)}px;
    height: ${({ $size }) => Math.round($size * 0.5)}px;
  }
`;

const AgentLogoWrapper = styled.div<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  min-width: ${({ $size }) => $size}px;
  border-radius: ${avatarSquircle.borderRadius};
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  flex-shrink: 0;
  overflow: hidden;
`;

const AgentLogo = styled.img<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  object-fit: contain;
  display: block;
`;

export const Avatar: React.FC<AvatarProps> = ({ role, size = 32, animated = false }) => {
  /* Resolution policy for the assistant message avatar:
   *   - 처리 중(`animated`)  → `assets.workspaceModes.agent.avatar` —
   *     모션 아바타(samsung `agent-bubble.gif`). 선언 안 한 테넌트는
   *     아래 정적 마크로 fallback.
   *   - 완료/idle(`!animated`) → `assets.agentLogo` — 정적 브랜드 마크
   *     (samsung `agent-bubble.png`). 모든 manifest 가 REQUIRED 로 선언하는
   *     슬롯이라 항상 존재(B2C 는 `/agent_logo.png`).
   * 이렇게 하면 "요청 → 타임라인 → 최종답변 스트리밍" 동안만 모션,
   * 답변 완료 시 정적 이미지로 바뀐다. 모션 아바타가 없는 테넌트는 두 상태
   * 모두 정적이라 회귀 없음. */
  const tenant = useTenant();
  if (role === 'assistant') {
    const modeAvatar = resolveWorkspaceModeAvatar(tenant, 'agent');
    const animatedSrc = modeAvatar?.src ?? tenant.assets.agentLogo;
    const staticSrc = tenant.assets.agentLogo;
    const src = animated ? animatedSrc : staticSrc;
    const alt = modeAvatar?.alt ?? `${tenant.identity.appName} agent`;
    return (
      <AgentLogoWrapper $size={size}>
        <AgentLogo src={src} alt={alt} $size={size} />
      </AgentLogoWrapper>
    );
  }
  return (
    <UserWrapper $size={size}>
      <User />
    </UserWrapper>
  );
};
